import React from 'react';
import {
  Box, Chip, CircularProgress, Divider, IconButton, LinearProgress, ListItemIcon, ListItemText, Menu,
  MenuItem, Paper, Tooltip, Typography,
} from '@mui/material';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReplayIcon from '@mui/icons-material/Replay';
import BarChartIcon from '@mui/icons-material/BarChart';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import TableRowsIcon from '@mui/icons-material/TableRows';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import { useTranslation } from 'react-i18next';
import PlotlyWidget from './PlotlyWidget';
import WidgetTable from './WidgetTable';
import { vizTypesForWidget } from '../../utilityFunctions/dashboardApi';
import GuidedFailureCard from './nl/GuidedFailureCard';
import { stageLabel } from './nl/stageLabels';

export const DRAG_HANDLE_CLASS = 'dash-widget-drag-handle';

/** Human label for a viz type, translated when a key exists. */
export const vizLabel = (t, vizType) => t(`Dashboards.vizTypes.${vizType}`, { defaultValue: vizType });

/**
 * One dashboard cell: header (drag handle, title, badges, chart-type menu,
 * refresh), body (Plotly figure, or the table fallback), footer (caption /
 * status note / warnings).
 *
 * @param widget        WidgetSpec from the dashboard spec
 * @param data          per-widget load state: { loading, error, response: WidgetDataResponse }
 * @param capabilities  CapabilityRegistry from GET /capabilities (may be null)
 * @param canEdit       owner on the current version → chart-type menu + drag handle
 * @param onChangeVizType(widgetId, vizType) emits a set_viz_type EditOp upstream
 * @param onRefresh(widgetId)                re-queries with refresh=true (bypass cache)
 * @param extraActions  optional header nodes (F2 mounts provenance here)
 * @param canRepair     owner on the current version → repair actions (also on the read-only narrow layout)
 * @param repairing     { stage } while a repair run of this widget is in flight, else null
 * @param repairDisabled another run is in flight (runs are serialized)
 * @param onRepair(widget, action)  action { type: 'retry' | 'guided' | 'expert' | 'suggestion', suggestion? }
 * @param diagnosis     plain-language diagnosis of a failed widget (spec, latest repair, or fetched), or null
 * @param diagnosisLoading  a lazy POST …/diagnose is in flight
 * @param onNeedDiagnosis(widget)  asked once when a failed widget has no diagnosis yet
 */
export default function WidgetFrame({
  widget, data, capabilities, canEdit, editPending, onChangeVizType, onRefresh, extraActions,
  canRepair, repairing, repairDisabled, onRepair, diagnosis, diagnosisLoading, onNeedDiagnosis,
}) {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [moreAnchor, setMoreAnchor] = React.useState(null);
  const [showTable, setShowTable] = React.useState(false);

  const state = data || { loading: true };
  const resp = state.response || null;
  const figure = resp ? resp.figure_json : null;
  const columns = resp ? resp.columns : [];
  const rows = resp ? resp.rows : [];

  const respStatus = resp ? String(resp.status || '').toUpperCase() : null;
  const notFilterable = widget.filterable === false || (resp && resp.filterable === false);
  const bindingStatus = resp && resp.binding_status ? String(resp.binding_status).toUpperCase() : null;
  const stale = bindingStatus === 'STALE' || String(widget.status || '').toUpperCase() === 'STALE';
  // WidgetDataResponse.status: OK | EMPTY | DEGRADED | ERROR.
  const buildFailure = widget.build_failure || null;
  // FAILED at build time: no binding (backend keeps build_failure; legacy specs only a status_note).
  const buildFailed = !!buildFailure
    || (!widget.binding_hash && (!!widget.status_note || String(widget.status || '').toUpperCase() === 'FAILED'));
  const failed = buildFailed || !!state.error || respStatus === 'ERROR' || respStatus === 'FAILED' || bindingStatus === 'FAILED';
  const repairable = !!(canRepair && onRepair);
  const needsDiagnosis = failed && repairable && !diagnosis && !diagnosisLoading && !state.loading && !!onNeedDiagnosis;
  React.useEffect(() => {
    if (needsDiagnosis) onNeedDiagnosis(widget);
  }, [needsDiagnosis]); // eslint-disable-line react-hooks/exhaustive-deps
  const warnings = (resp && Array.isArray(resp.warnings)) ? resp.warnings : [];
  const currentViz = widget.visual && widget.visual.viz_type;
  // A chart was intended (non-table visual) but the server sent no figure: say so instead of
  // silently showing the table (sidecar down / circuit open, shape mismatch, integrity failure).
  const chartIntended = !!currentViz && currentViz !== 'table';
  const chartUnavailable = !!resp && !failed && !figure && respStatus !== 'EMPTY' && (rows || []).length > 0
    && (chartIntended || warnings.includes('chart_unavailable'));
  const integrityFailed = warnings.includes('figure_integrity_failed');
  const chartUnavailableReason = (resp && resp.figure_error)
    || (integrityFailed ? t('Dashboards.badges.chartIntegrityHint') : t('Dashboards.badges.chartUnavailableHint'));

  const vizOptions = React.useMemo(() => vizTypesForWidget(capabilities, widget, resp), [capabilities, widget, resp]);
  const table = <WidgetTable columns={columns} rows={rows} />;
  const tableWithNotice = (message, detail) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Tooltip title={detail || ''}>
        <Typography variant="caption" color="warning.dark" data-testid="chart-unavailable-notice"
          sx={{ px: 0.75, pt: 0.25, flexShrink: 0 }} noWrap>
          {message}
        </Typography>
      </Tooltip>
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>{table}</Box>
    </Box>
  );

  const pickViz = (vt) => {
    setMenuAnchor(null);
    if (vt !== currentViz) onChangeVizType(widget.widget_id, vt);
  };

  let body;
  if (state.loading && !resp) {
    body = (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  } else if (failed) {
    const message = (buildFailure && buildFailure.message) || (buildFailed && widget.status_note)
      || state.error || (resp && resp.error) || t('Dashboards.widget.loadFailed');
    // Scrolls inside the cell: a KPI cell is ~140px tall.
    body = (
      <GuidedFailureCard
        diagnosis={diagnosis}
        diagnosisLoading={diagnosisLoading}
        failure={{ stage: buildFailure && buildFailure.stage, message, lastSql: buildFailure && buildFailure.last_sql }}
        canRepair={repairable}
        disabled={!!repairDisabled || !!repairing}
        onAction={(action) => onRepair(widget, action)}
      />
    );
  } else if (showTable && figure) {
    body = table;
  } else if (!figure) {
    body = chartUnavailable
      ? tableWithNotice(
        t('Dashboards.widget.chartUnavailableNotice', { vizType: vizLabel(t, currentViz || 'chart') }),
        chartUnavailableReason)
      : table;
  } else {
    body = (
      <PlotlyWidget
        figure={figure}
        filename={widget.title}
        fallback={(err) => tableWithNotice(t('Dashboards.widget.chartRenderFailedNotice'),
          err && err.message ? err.message : String(err || ''))}
      />
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 2,
        bgcolor: '#fff', opacity: editPending ? 0.7 : 1 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, pt: 0.75, pb: 0.25, minHeight: 40 }}>
        {canEdit && (
          <Tooltip title={t('Dashboards.widget.dragHint')}>
            <Box className={DRAG_HANDLE_CLASS} sx={{ cursor: 'move', display: 'flex', color: 'text.disabled' }}>
              <DragIndicatorIcon fontSize="small" />
            </Box>
          </Tooltip>
        )}
        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, flexShrink: 1, minWidth: 0 }} title={widget.title}>
          {widget.title || widget.widget_id}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap', overflow: 'hidden', ml: 0.5 }}>
          {notFilterable && (
            <Tooltip title={t('Dashboards.badges.notFilterableHint')}>
              <Chip size="small" variant="outlined" label={t('Dashboards.badges.notFilterable')} />
            </Tooltip>
          )}
          {chartUnavailable && (
            <Tooltip title={chartUnavailableReason}>
              <Chip size="small" color="warning" variant="outlined" label={t('Dashboards.badges.chartUnavailable')} />
            </Tooltip>
          )}
          {stale && (
            <Tooltip title={t('Dashboards.badges.staleHint')}>
              <Chip size="small" color="warning" label={t('Dashboards.badges.stale')} />
            </Tooltip>
          )}
          {resp && resp.from_cache && (
            <Tooltip title={t('Dashboards.badges.cachedHint')}>
              <Chip size="small" variant="outlined" label={t('Dashboards.badges.cached')} sx={{ color: 'text.secondary' }} />
            </Tooltip>
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        {state.loading && resp && <CircularProgress size={14} sx={{ mr: 0.5 }} />}
        {extraActions}
        {figure && (
          <Tooltip title={showTable ? t('Dashboards.widget.showChart') : t('Dashboards.widget.showTable')}>
            <IconButton size="small" onClick={() => setShowTable((s) => !s)}>
              {showTable ? <InsertChartOutlinedIcon fontSize="small" /> : <TableRowsIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        {canEdit && vizOptions.length > 1 && (
          <>
            <Tooltip title={t('Dashboards.widget.chartType')}>
              <span>
                <IconButton size="small" disabled={editPending} onClick={(e) => setMenuAnchor(e.currentTarget)}>
                  <BarChartIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
              <MenuItem disabled dense>
                <Typography variant="caption">{t('Dashboards.widget.chartTypeMenuTitle')}</Typography>
              </MenuItem>
              <Divider />
              {vizOptions.map((v) => (
                <MenuItem key={v.viz_type} dense selected={v.viz_type === currentViz} onClick={() => pickViz(v.viz_type)}>
                  <ListItemIcon>{v.viz_type === currentViz ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
                  <ListItemText>{vizLabel(t, v.viz_type)}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
        <Tooltip title={t('Dashboards.widget.refresh')}>
          <span>
            <IconButton size="small" disabled={state.loading || buildFailed} onClick={() => onRefresh(widget.widget_id)}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {repairable && (
          <>
            <Tooltip title={t('Dashboards.repair.menu')}>
              <IconButton size="small" onClick={(e) => setMoreAnchor(e.currentTarget)} aria-label={t('Dashboards.repair.menu')}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={moreAnchor} open={!!moreAnchor} onClose={() => setMoreAnchor(null)}>
              <MenuItem dense disabled={!!repairDisabled || !!repairing}
                onClick={() => { setMoreAnchor(null); onRepair(widget, { type: 'expert' }); }}>
                <ListItemIcon><BuildOutlinedIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('Dashboards.repair.expert')}</ListItemText>
              </MenuItem>
              <MenuItem dense disabled={!!repairDisabled || !!repairing}
                onClick={() => { setMoreAnchor(null); onRepair(widget, { type: 'retry' }); }}>
                <ListItemIcon><ReplayIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{buildFailed ? t('Dashboards.repair.retry') : t('Dashboards.repair.regenerate')}</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
      {repairing && (
        <Box sx={{ px: 1, pb: 0.5, minWidth: 0 }} data-testid="widget-repairing">
          <Typography variant="caption" color="primary" display="block" noWrap>
            {t('Dashboards.repair.running', { stage: stageLabel(t, repairing.stage) || t('Dashboards.run.stages.repair') })}
          </Typography>
          <LinearProgress sx={{ borderRadius: 1 }} />
        </Box>
      )}
      <Box sx={{ flexGrow: 1, minHeight: 0, px: 0.5 }}>{body}</Box>
      {((widget.status_note && !buildFailed) || (widget.caption && widget.caption.text) || warnings.length > 0) && (
        <Box sx={{ px: 1.25, pb: 0.75, pt: 0.25 }}>
          {widget.status_note && !buildFailed && (
            <Typography variant="caption" color="warning.dark" display="block" noWrap title={widget.status_note}>
              {widget.status_note}
            </Typography>
          )}
          {widget.caption && widget.caption.text && (
            <Typography variant="caption" color="text.secondary" display="block" noWrap title={widget.caption.text}>
              {widget.caption.text}
            </Typography>
          )}
          {warnings.length > 0 && (
            <Tooltip title={warnings.join(' · ')}>
              <Typography variant="caption" color="text.disabled" display="block" noWrap>
                {t('Dashboards.widget.warnings', { count: warnings.length })}
              </Typography>
            </Tooltip>
          )}
        </Box>
      )}
    </Paper>
  );
}
